import React from "react"
import { useTranslation } from "react-i18next"

import { isNumberValid } from "@/base/type-guards"
import { useIsPending } from "@/redux/requests.reducer"
import {
  getActiveFarmNameFromState,
  getDemoFarmIdOrActiveFarmIdFromState,
} from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  getActiveFarmSubscriptionDueDate,
  getFarmAccountSubscriptionLockDate,
  getRealFarmCountFromState,
  isDateWithinSubscriptionWarningThreshold,
} from "@/redux/user-farms.selectors"
import type { TestId } from "@/theme"
import {
  AlertBodyText,
  AlertCard,
  AlertDialog,
  AlertDialogScrollView,
  AppText,
  Box,
  COLORS,
  Column,
  Heading,
  IconButton,
  LinkText,
  Row,
  useClipboard,
  useIsDarkMode,
} from "@/theme"
import { makeDateFormatOptions } from "@/translations"
import { Link } from "expo-router"
import { ActiveFarmCycler } from "./ActiveFarmIndicator"

const SALES_EMAIL = "sales@farmhq.com"
const SPACE = "%20"
const SUBJECT = "Subscription Renewal".replaceAll(" ", SPACE)

const DATE_FORMAT_PARAMS = makeDateFormatOptions({
  // numeric: "auto",
  dateStyle: "medium",
  timeStyle: "short",
})

function ContactActions() {
  const onCopy = useClipboard()

  return (
    <Box mt={"$4"}>
      <Row>
        <Link asChild href={`mailto:${SALES_EMAIL}?subject=${SUBJECT}`}>
          <LinkText>{SALES_EMAIL}</LinkText>
        </Link>
        <Box ml={"$2"}>
          <IconButton
            IconComponent={"Clipboard"}
            variant={"text"}
            onPress={() => onCopy(SALES_EMAIL)}
          />
        </Box>
      </Row>
    </Box>
  )
}

/**
 * Warns user if their farm account is about to be locked due to non-payment.
 */
export function FarmAccountNonPaymentWarningDialog() {
  const { t } = useTranslation("subscriptions")
  const dueDate = useRootSelector(getActiveFarmSubscriptionDueDate)
  const activeFarmId = useRootSelector(getDemoFarmIdOrActiveFarmIdFromState)
  React.useEffect(() => {
    if (isNumberValid(activeFarmId)) {
      setIsOpen(true)
    }
  }, [activeFarmId])

  const [isOpen, setIsOpen] = React.useState(true)

  if (typeof dueDate !== "number") {
    return null
  }
  if (!isDateWithinSubscriptionWarningThreshold(dueDate)) {
    return null
  }
  const handleClose = () => setIsOpen(false)

  let titleText: string
  let messageText: string
  let testId: TestId
  const isDueDateInTheFuture =
    new Date(dueDate).getTime() > new Date().getTime()

  if (isDueDateInTheFuture) {
    testId = "subscriptionDueReminder"
    titleText = t("dueDateUpcomingTitle")
    messageText = t("dueDateUpcomingMessageWithDueDate", {
      dueDate,
      ...DATE_FORMAT_PARAMS,
    })
  } else {
    // Due date is in the past
    testId = "subscriptionOverdueWarning"
    titleText = t("dueDatePassedTitle")
    messageText = t("dueDatePassedMessagewithLockDate", {
      lockDate: getFarmAccountSubscriptionLockDate(dueDate),
      ...DATE_FORMAT_PARAMS,
    })
  }

  return (
    <AlertDialog
      IconComponent={"Warning"}
      id={testId}
      isOpen={isOpen}
      titleElement={titleText}
      onClose={handleClose}
    >
      <AlertDialogScrollView>
        <AlertCard mb={"$4"}>
          <AlertBodyText>
            {t("dueDateWithValue", { value: dueDate, ...DATE_FORMAT_PARAMS })}
          </AlertBodyText>
        </AlertCard>
        <Box mb={"$4"}>
          <AppText>{messageText}</AppText>
        </Box>
        <Box mb={"$4"}>
          <AppText>{t("eitherPayOrContactUs")}</AppText>
        </Box>
        <ContactActions />
      </AlertDialogScrollView>
    </AlertDialog>
  )
}

/**
 * Lock user out of the app if they have not paid their subscription
 */
export function AccountLockedDueToNonPayment() {
  const { t } = useTranslation("subscriptions")
  const numFarms = useRootSelector(getRealFarmCountFromState)
  const activeFarmName = useRootSelector(getActiveFarmNameFromState)

  const isChangingFarms = useIsPending("SetActiveFarmId", "LoadApp")
  const isDark = useIsDarkMode()

  return (
    <Column space={"$4"}>
      <Heading>{t("accountLockedTitle")}</Heading>
      <AppText>{t("accountLockedMessage")}</AppText>
      <ContactActions />
      {numFarms > 1 ? (
        <Row
          bg={isDark ? COLORS.$gray[600] : COLORS.$gray[200]}
          justifyContent={"space-between"}
          minW={"$xs"}
          p={"$3"}
          rounded={"$default"}
        >
          <Box flex={1}>
            <AppText>{t("account", { ns: "common" })}</AppText>
            <AppText colorScheme={"secondary"}>{activeFarmName}</AppText>
          </Box>
          <ActiveFarmCycler isChangingFarms={isChangingFarms} />
        </Row>
      ) : null}
    </Column>
  )
}
